import React, { Fragment } from "react";
import { useNavigate, useParams, NavLink } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Card, CardBody, CardHeader, Col, Container, Row, Label, Input, Spinner, Table } from 'reactstrap';
import BreadCrumb from '../components/Common/BreadCrumb';
import dateFormat from "dateformat";

import { createDeepClone } from "../utils/index";
import MyDateRenderer from "../components/MyDateRenderer";
import IsoServiceDocumentGrid from "../components/IsoServiceDocumentGrid";

import CardLoader from "../components/CardLoader";
import ActionList from "../components/ActionList";

import { useDispatch, useSelector } from "react-redux";
import * as isoRequestActions from "../actions/IsoRequestActions";
import * as commonActions from "../actions/CommonActions";

const IsoCertificateEdit  = (props) => {
    const { register, clearErrors, reset, trigger, formState: { errors } } = useForm({mode: 'all'});

    const navigate = useNavigate();
    const { id } = useParams();

    const dispatch = useDispatch();

    const loading = useSelector((state) => state.iso.loadingCertificate);
    const fbo = useSelector((state) => state.iso.certificate);
    const user = useSelector((state) => state.login.user);
    
    const [fboState, setFBOState] = React.useState(fbo);
    const [isDirty, setIsDirty] = React.useState(false);
    const [refreshAction, setRefreshAction] = React.useState(false);

    const [loadingLocal, setLoadingLocal] = React.useState(false);

    React.useEffect(() => {
	    dispatch(isoRequestActions.loadIsoCertificate(id, navigate));
    }, [id]);

    React.useEffect(() => {
		setFBOState(fbo);
        setIsDirty(false);
        setRefreshAction(!refreshAction);
                
        if(fbo) {
            reset(fbo.certificate);
        }
        
        clearErrors();
    }, [fbo]);

    const reloadAction = () => {
        setRefreshAction(!refreshAction);
    }

    const onChangeDocument = (isProduct) => {
        setRefreshAction(!refreshAction);
    }

    const getFormattedDate = (value) => {
        if(value) {
            return dateFormat(value, "mm/dd/yyyy");
        }
        return "";
    }

    const onCertificateChange = (name, value) => {
        var copy_fboState = createDeepClone(fboState);

        if(value) {
            if(name === "surveillance1Days" || name === "surveillance2Days"|| name === "certificateExpiryDays") {
                value = value.replace(/[^0-9]/g, '');
            }
        }

        if(value) {
            copy_fboState.certificate[name] = value;
        }
        else if(value === false) {
            copy_fboState.certificate[name] = false;
        }
        else {
            copy_fboState.certificate[name] = null;
        }
        
        setFBOState(copy_fboState);
        setIsDirty(true);
    }

    const onSaveCertificate = async() => {
        const valid = await trigger();
        if(valid) {
            dispatch(isoRequestActions.saveIsoCertificate(fboState, navigate));
        }
    }

    const onGenerateWithdrawalLetter = async() => {
        setLoadingLocal(true);
        var data = await isoRequestActions.generateWithdrawalLetter(id);
        setLoadingLocal(false);

        if(data.success) {
            dispatch(commonActions.setSuccessMessage(data.successMessage));
        }
        else{
            dispatch(commonActions.setErrorMessage(data.errorMessage));
        }

        reloadAction();
    }

    const onCancelCertificate = () => {
        setFBOState(fbo);

        setIsDirty(false);
        clearErrors();
    }
    
    return(
        <Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title={"Edit Certificate"} />
                    <Row>
                        <Col lg={fboState && fboState.certificate.id > 0 ? 9 : 12}>
                            <CardLoader loading={loading || loadingLocal} />

                            <Card className="mb-0">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">Certificate</h5>
                                    {fboState && fboState.canSave &&
                                    <div className="flex-shrink-0">
                                        <div className="d-flex flex-wrap gap-2">
                                            <button className="btn btn-primary btn-label"
                                                disabled={loading || loadingLocal || !isDirty}
                                                onClick={onSaveCertificate}
                                            >
                                                <i className="ri-save-line label-icon align-middle"></i> Save Certificate
                                            </button>
                                            <button className="btn btn-danger btn-label"
                                                disabled={loading || loadingLocal || !isDirty}
                                                onClick={onCancelCertificate}
                                            >
                                                <i className="ri-close-line label-icon align-middle"></i> Cancel
                                            </button>
                                            <button className="btn btn-dark btn-label"
                                                disabled={loading || loadingLocal}
                                                onClick={() => onGenerateWithdrawalLetter()}
                                            >
                                                <i className="ri-check-double-line label-icon align-bottom"></i> Generate Withdrawal Letter
                                            </button>
                                        </div>
                                    </div>
                                    }
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0">
                                    <Row className="gy-2">
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Certificate #</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.certificate.certificateNumber) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Accreditation Type</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && fboState.certificate.accreditationType) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Status</Label>
                                            <select 
                                                className={`form-select ${errors.certificateStatus ? "is-invalid" : ""}`} disabled={fboState && fboState.canSave ? false : true}
                                                value={(fboState && fboState.certificate.certificateStatus) || ""}
                                                title={errors.certificateStatus?.message}
                                                {...register("certificateStatus", { required: "Status is required" })}
                                                onChange={e => onCertificateChange("certificateStatus", e.target.value)}
                                            >
                                                <option value={"Active"}>Active</option>
                                                <option value={"Suspended"}>Suspended</option>
                                                <option value={"Withdrawal"}>Withdrawal</option>
                                                <option value={"Inactive"}>Inactive</option>
                                            </select>
                                        </Col>
                                    </Row>
                                    <Row className="gy-2">
                                        <Col xxl={4} md={6}>
                                        <Label className="form-label">Certificate Issue Date</Label>
                                        <input type="text" className="form-control" readOnly={true}
                                            value={(fboState && getFormattedDate(fboState.certificate.certificateIssueDate)) || ""}
                                        />
                                    </Col>
                                    </Row>
                                    {fboState && fboState.certificate && !fboState.certificate.isUnaccredited &&
                                    <Fragment>
                                        <Row className="gy-2">
                                            <Col xxl={4} md={6}>
                                                <Label className="form-label">1st Surveillance Date</Label>
                                                <input type="text" className="form-control" readOnly={true}
                                                    value={(fboState && getFormattedDate(fboState.certificate.surveillance1Date)) || ""}
                                                />
                                            </Col>
                                            <Col xxl={4} md={6}>
                                                <Label className="form-label">Notification Start (before)<span className="required">*</span></Label>
                                                <input type="text" className={`form-control ${errors.surveillance1Days ? "is-invalid" : ""}`} readOnly={fboState && fboState.canSave ? false : true}
                                                    value={(fboState && fboState.certificate.surveillance1Days) || ""}
                                                    title={errors.surveillance1Days?.message}
                                                    {...register("surveillance1Days", { required: "Notification Start (before) is required" })}
                                                    onChange={e => onCertificateChange("surveillance1Days", e.target.value)}
                                                />
                                            </Col>
                                            <Col xxl={4} md={6}>
                                                <Label className="form-label">Notification Duration<span className="required">*</span></Label>
                                                <select 
                                                    className={`form-select ${errors.surveillance1Duration ? "is-invalid" : ""}`} disabled={fboState && fboState.canSave ? false : true}
                                                    value={(fboState && fboState.certificate.surveillance1Duration) || ""}
                                                    title={errors.surveillance1Duration?.message}
                                                    {...register("surveillance1Duration", { required: "Notification Duration is required" })}
                                                    onChange={e => onCertificateChange("surveillance1Duration", e.target.value)}
                                                >
                                                    <option value={""}>...</option>
                                                    <option value={"7"}>Weekly</option>
                                                    <option value={"1"}>Daily</option>
                                                    <option value={"0"}>Snooze</option>
                                                </select>
                                            </Col>
                                        </Row>
                                        <Row className="gy-2">
                                            <Col xxl={4} md={6}>
                                                <Label className="form-label">2nd Surveillance Date</Label>
                                                <input type="text" className="form-control" readOnly={true}
                                                    value={(fboState && getFormattedDate(fboState.certificate.surveillance2Date)) || ""}
                                                />
                                            </Col>
                                            <Col xxl={4} md={6}>
                                                <Label className="form-label">Notification Start (before)<span className="required">*</span></Label>
                                                <input type="text" className={`form-control ${errors.surveillance2Days ? "is-invalid" : ""}`} readOnly={fboState && fboState.canSave ? false : true}
                                                    value={(fboState && fboState.certificate.surveillance2Days) || ""}
                                                    title={errors.surveillance2Days?.message}
                                                    {...register("surveillance2Days", { required: "Notification Start (before) is required" })}
                                                    onChange={e => onCertificateChange("surveillance2Days", e.target.value)}
                                                />
                                            </Col>
                                            <Col xxl={4} md={6}>
                                                <Label className="form-label">Notification Duration<span className="required">*</span></Label>
                                                <select 
                                                    className={`form-select ${errors.surveillance2Duration ? "is-invalid" : ""}`} disabled={fboState && fboState.canSave ? false : true}
                                                    value={(fboState && fboState.certificate.surveillance2Duration) || ""}
                                                    title={errors.surveillance2Duration?.message}
                                                    {...register("surveillance2Duration", { required: "Notification Duration is required" })}
                                                    onChange={e => onCertificateChange("surveillance2Duration", e.target.value)}
                                                >
                                                    <option value={""}>...</option>
                                                    <option value={"7"}>Weekly</option>
                                                    <option value={"1"}>Daily</option>
                                                    <option value={"0"}>Snooze</option>
                                                </select>
                                            </Col>
                                        </Row>
                                    </Fragment>
                                    }
                                    <Row className="gy-2">
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Certificate Expiry Date</Label>
                                            <input type="text" className="form-control" readOnly={true}
                                                value={(fboState && getFormattedDate(fboState.certificate.certificateExpiryDate)) || ""}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Notification Start (before)<span className="required">*</span></Label>
                                            <input type="text" className={`form-control ${errors.certificateExpiryDays ? "is-invalid" : ""}`} readOnly={fboState && fboState.canSave ? false : true}
                                                value={(fboState && fboState.certificate.certificateExpiryDays) || ""}
                                                title={errors.certificateExpiryDays?.message}
                                                {...register("certificateExpiryDays", { required: "Notification Start (before) is required" })}
                                                onChange={e => onCertificateChange("certificateExpiryDays", e.target.value)}
                                            />
                                        </Col>
                                        <Col xxl={4} md={6}>
                                            <Label className="form-label">Notification Duration<span className="required">*</span></Label>
                                            <select 
                                                className={`form-select ${errors.certificateExpiryDuration ? "is-invalid" : ""}`} disabled={fboState && fboState.canSave ? false : true}
                                                value={(fboState && fboState.certificate.certificateExpiryDuration) || ""}
                                                title={errors.certificateExpiryDuration?.message}
                                                {...register("certificateExpiryDuration", { required: "Notification Duration is required" })}
                                                onChange={e => onCertificateChange("certificateExpiryDuration", e.target.value)}
                                            >
                                                <option value={""}>...</option>
                                                <option value={"7"}>Weekly</option>
                                                <option value={"1"}>Daily</option>
                                                <option value={"0"}>Snooze</option>
                                            </select>
                                        </Col>
                                    </Row>
                                    <Row className="gy-2">
                                        
                                    </Row>
                                </CardBody>
                            </Card>
                            <Card className="mb-0">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">
                                        Certificate History
                                    </h5>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0 card-text">
                                    <Row className="gy-2">
                                        <Col xxl={12} md={12}>
                                            <Table className="table-hover table-bordered align-middle mb-0">
                                                <thead className="table-primary">
                                                    <tr>
                                                        <th>Certificate #</th>
                                                        <th>Certificate Issue Date</th>
                                                        {fboState && fboState.certificate && !fboState.certificate.isUnaccredited &&
                                                        <Fragment>
                                                            <th>1st Surveillance Date</th>
                                                            <th>2nd Surveillance Date</th>
                                                        </Fragment>
                                                        }
                                                        <th>Certificate Expiry Date</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fboState && fboState.historyList.length == 0 && <tr><td colSpan={6}>No History</td></tr>}
                                                    {fboState && fboState.historyList.map(function(item, index){
                                                        return(
                                                            <tr key={index}>
                                                                <td>{item.certificateNumber}</td>
                                                                <td><MyDateRenderer value={item.certificateIssueDate} format="mm/dd/yyyy" /></td>
                                                                {fboState && fboState.certificate && !fboState.certificate.isUnaccredited &&
                                                                <Fragment>
                                                                    <td><MyDateRenderer value={item.surveillance1Date} format="mm/dd/yyyy" /></td>
                                                                    <td><MyDateRenderer value={item.surveillance2Date} format="mm/dd/yyyy" /></td>
                                                                </Fragment>
                                                                }
                                                                <td><MyDateRenderer value={item.certificateExpiryDate} format="mm/dd/yyyy" /></td>
                                                                <td>{item.certificateStatus}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                            <Card className="mb-0">
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">
                                        Certificate Tickets
                                    </h5>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0 card-text">
                                    <Row className="gy-2">
                                        <Col xxl={12} md={12}>
                                            <Table className="table-hover table-bordered align-middle mb-0">
                                                <thead className="table-primary">
                                                    <tr>
                                                        <th>Ticket #</th>
                                                        <th>Request Type</th>
                                                        <th>Status</th>
                                                        <th>Certificate #</th>
                                                        {user.isLinkedToCrm && <th>CRM Invoice #</th>}
                                                        {user.isLinkedToCrm && <th>Tax Invoice #</th>}
                                                        {user.isLinkedToCrm && <th>Reference #</th>}
                                                        <th>Action Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fboState && fboState.requestList.length == 0 && <tr><td colSpan={8}>No Ticket(s)</td></tr>}
                                                    {fboState && fboState.requestList.map(function(item, index){
                                                        return(
                                                            <tr key={index}>
                                                                <td>
                                                                    <NavLink  to={`/iso-request-edit/${item.id}`}>{item.requestNumber}</NavLink>
                                                                </td>
                                                                <td>
                                                                    {item.requestTypeName?.split(",").map((name, index) => (
                                                                        <div key={index}>
                                                                            <span className="badge bg-success">
                                                                                {name.trim()}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </td>
                                                                <td><span className="badge" style={{backgroundColor: item.statusColorCode}}>{item.statusName}</span></td>
                                                                <td>
                                                                    {item.certificateNumber?.split(",").map((name, index) => (
                                                                        <div key={index}>
                                                                            <span className="badge bg-primary">
                                                                                {name.trim()}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </td>
                                                                {user.isLinkedToCrm && <td>{item.crmInvoiceNumber}</td>}
                                                                {user.isLinkedToCrm && <td>{item.taxInvoiceNumber}</td>}
                                                                {user.isLinkedToCrm && <td>{item.referenceNumber}</td>}
                                                                <td><MyDateRenderer value={item.lastActionDate} /></td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                            {fboState && fboState.certificate.id > 0 &&
                                <IsoServiceDocumentGrid 
                                    serviceId={-10}
                                    refId={fboState.certificate.id}
                                    refName={"ISO_CERTIFICATE"}
                                    parentId={fboState.certificate.id}
                                    parentName={"ISO_CERTIFICATE"}
                                    isProduct={false}
                                    refreshAction={refreshAction}
                                    collapsed={"false"}
                                    canChange={fboState.canSave}
                                    onChange={onChangeDocument}

                                    showSurveillance1Document={fboState.showSurveillance1Document}
                                    canEditSurveillance1Document={fboState.canEditSurveillance1Document}
                                    isSurveillance1DocumentRequired={fboState.isSurveillance1DocumentRequired}

                                    showSurveillance2Document={fboState.showSurveillance2Document}
                                    canEditSurveillance2Document={fboState.canEditSurveillance2Document}
                                    isSurveillance2DocumentRequired={fboState.isSurveillance2DocumentRequired}

                                    showRecertificationDocument={fboState.showRecertificationDocument}
                                    canEditRecertificationDocument={fboState.canEditRecertificationDocument}
                                    isRecertificationDocumentRequired={fboState.isRecertificationDocumentRequired}

                                    showSuspensionDocument={fboState.showSuspensionDocument}
                                    canEditSuspensionDocument={fboState.canEditSuspensionDocument}
                                    isSuspensionDocumentRequired={fboState.isSuspensionDocumentRequired}

                                    showWithdrawalDocument={fboState.showWithdrawalDocument}
                                    canEditWithdrawalDocument={fboState.canEditWithdrawalDocument}
                                    isWithdrawalDocumentRequired={fboState.isWithdrawalDocumentRequired}
                                />
                            }
                            {user && user.isAdmin &&
                            <Card>
                                <CardHeader className="border-0 d-flex align-items-center">
                                    <h5 className="card-title mb-0 flex-grow-1">
                                        Certificate Notifications
                                    </h5>
                                </CardHeader>
                                <CardBody className="border border-dashed border-end-0 border-start-0 card-text">
                                    <Row className="gy-2">
                                        <Col xxl={12} md={12}>
                                            <Table className="table-hover table-bordered align-middle mb-0">
                                                <thead className="table-primary">
                                                    <tr>
                                                        <th>Notification Type</th>
                                                        <th>Notification Status</th>
                                                        <th>Notification Date</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fboState && fboState.notificationList.length == 0 && <tr><td colSpan={8}>No Ticket(s)</td></tr>}
                                                    {fboState && fboState.notificationList.map(function(item, index){
                                                        return(
                                                            <tr key={index}>
                                                                <td>{item.notificationType}</td>
                                                                <td>{item.status}</td>
                                                                <td><MyDateRenderer value={item.notificationDate} format={"mm/dd/yyyy"} /></td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                            }
                        </Col>
                        
                        {fboState && fboState.certificate.id > 0 &&
                        <Col lg={3}>
                            <ActionList id={fboState.certificate.id} name={"CERTIFICATE"} refreshAction={refreshAction} />
                        </Col>
                        }
                    </Row>
                </Container>
            </div>
        </Fragment>
    );
}

export default IsoCertificateEdit;